import type { ModuleWithProgress } from "@/lib/api-types";
import { cn, formatDuration } from "@/lib/utils";
import { Check, Circle, Play, Star } from "lucide-react";

interface ModuleItemProps {
	module: ModuleWithProgress;
	isCurrent: boolean;
	onClick: () => void;
}

export function ModuleItem({ module, isCurrent, onClick }: ModuleItemProps) {
	const status = module.progress?.status ?? "not_started";

	const statusConfig = {
		completed: {
			icon: <Check className="h-4 w-4 text-success" />,
			border: "border-l-success",
			textClass: "text-secondary",
		},
		in_progress: {
			icon: <Play className="h-4 w-4 text-accent" />,
			border: "border-l-accent",
			textClass: "text-primary font-medium",
		},
		not_started: {
			icon: <Circle className="h-4 w-4 text-muted" />,
			border: "border-l-transparent",
			textClass: "text-secondary",
		},
	};

	const config = isCurrent ? statusConfig.in_progress : statusConfig[status];

	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				"flex w-full items-center gap-3 border-l-2 px-4 py-3 text-left transition-colors duration-150",
				"hover:bg-bg-hover",
				config.border,
				isCurrent && "bg-accent-muted",
			)}
		>
			<span className="flex-shrink-0">{config.icon}</span>
			<div className="min-w-0 flex-1">
				<p className={cn("truncate text-sm", config.textClass)}>
					{module.position}. {module.title}
				</p>
				<p className="text-xs text-muted">{formatDuration(module.duration_sec)}</p>
			</div>
			{module.has_quiz && <Star className="h-3.5 w-3.5 flex-shrink-0 text-warning" />}
		</button>
	);
}
